import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, query, where, getDocs, deleteDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import CustomerEstimator from './CustomerEstimator';
import LandingPage from './LandingPage';

const firebaseConfig = {
  apiKey: "AIzaSyD3zBvJb98uxd3Bt1EFIpgSFv2Rta_mASk",
  authDomain: "fence-estimator-saas.firebaseapp.com",
  projectId: "fence-estimator-saas",
  storageBucket: "fence-estimator-saas.firebasestorage.app",
  messagingSenderId: "228487635583",
  appId: "1:228487635583:web:479641b13e1eb6a7bd554a"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

// YOUR ADMIN EMAIL - Only you can manage subscriptions
const ADMIN_EMAIL = "contact@simplyawesomemarketing.com";

const ZIP_MULTIPLIERS = {
  '100': 1.15, '101': 1.15, '102': 1.12, '103': 1.10, '104': 1.10,
  '105': 1.08, '106': 1.08, '107': 1.05, '108': 1.05, '109': 1.05,
  '200': 1.05, '201': 1.12, '202': 1.15, '203': 1.10, '204': 1.08,
  '205': 1.08, '206': 1.10, '207': 1.12, '208': 1.08, '209': 1.05,
  '210': 1.05, '211': 1.08, '212': 1.10, '213': 1.08, '214': 1.05,
  '215': 1.08, '216': 1.10, '217': 1.08, '218': 1.05, '219': 1.05,
  '220': 1.02, '221': 1.00, '222': 1.02, '223': 1.05, '224': 1.05,
  '225': 1.02, '226': 1.00, '227': 1.02, '228': 1.00, '229': 1.00,
  '300': 1.05, '301': 1.12, '302': 1.10, '303': 1.15, '304': 1.12,
  '305': 1.15, '306': 1.12, '307': 1.08, '308': 1.05, '309': 1.05,
  '310': 1.20, '311': 1.18, '312': 1.15, '313': 1.12, '314': 1.10,
  '315': 1.08, '316': 1.12, '317': 1.10, '318': 1.05, '319': 1.08,
  '320': 1.08, '321': 1.15, '322': 1.12, '323': 1.10, '324': 1.15,
  '325': 1.08, '326': 1.10, '327': 1.12, '328': 1.15, '329': 1.12,
  '330': 1.05, '331': 1.08, '332': 1.10, '333': 1.12, '334': 1.10,
  '335': 1.08, '336': 1.05, '337': 1.08, '338': 1.10, '339': 1.12
};

// Helper: Calculate days remaining in trial
const getDaysRemaining = (trialEndDate) => {
  if (!trialEndDate) return 0;
  const end = trialEndDate.toDate ? trialEndDate.toDate() : new Date(trialEndDate);
  const now = new Date();
  const diff = end - now;
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
};

// Helper: Check if subscription is active
const isSubscriptionActive = (subscription) => {
  if (!subscription) return false;
  const { status, trialEndDate, paymentStatus } = subscription;

  if (status === 'active' && paymentStatus === 'paid') return true;
  if (status === 'trial' && getDaysRemaining(trialEndDate) > 0) return true;
  return false;
};

function App() {
  const path = window.location.pathname;
  const estimatePrefix = '/estimate/';
  const isCustomerPage = path.startsWith(estimatePrefix) && path.replace(estimatePrefix, '').length > 0;
  const isLoginPage = path === '/login';
  const isRootPage = path === '/' || path === '';

  if (isCustomerPage) return <CustomerEstimator />;
  if (isRootPage) return <LandingPage />;
  return <AdminApp />;
}

function AdminApp() {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [authError, setAuthError] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [fences, setFences] = useState([]);
  const [showAddFence, setShowAddFence] = useState(false);
  const [newFence, setNewFence] = useState({ name: '', type: '', style: '', color: '', basePrice: '', image: null });
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [bulkImportProgress, setBulkImportProgress] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFence, setSelectedFence] = useState(null);
  const [propertyImageUrl, setPropertyImageUrl] = useState(null);
  const [fenceHeight, setFenceHeight] = useState(6);
  const [linearFeet, setLinearFeet] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [showPricingSettings, setShowPricingSettings] = useState(false);
  const [pricingSettings, setPricingSettings] = useState({ laborPercentage: 65, profitMargin: 15, materialMarkup: 0 });
  const [showCustomerLink, setShowCustomerLink] = useState(false);
  const [customerLinkCopied, setCustomerLinkCopied] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        setIsAdmin(currentUser.email === ADMIN_EMAIL);
        await loadSubscription(currentUser.uid);
        loadFences(currentUser.uid);
        if (currentUser.email === ADMIN_EMAIL) {
          loadAllUsers();
        }
      }
      setSubscriptionLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadSubscription = async (userId) => {
    try {
      const docRef = doc(db, 'companySettings', userId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setSubscription(docSnap.data());
        if (docSnap.data().pricingSettings) {
          setPricingSettings(docSnap.data().pricingSettings);
        }
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    }
  };

  const loadAllUsers = async () => {
    try {
      const q = query(collection(db, 'companySettings'));
      const snapshot = await getDocs(q);
      const users = [];
      snapshot.forEach((doc) => users.push({ id: doc.id, ...doc.data() }));
      setAllUsers(users);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadFences = async (userId) => {
    try {
      const q = query(collection(db, 'fences'), where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      const loadedFences = [];
      querySnapshot.forEach((doc) => loadedFences.push({ id: doc.id, ...doc.data() }));
      setFences(loadedFences);
    } catch (error) {
      console.error('Error loading fences:', error);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      if (isSignUp) {
        if (!companyName.trim()) { setAuthError('Please enter a company name'); return; }
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        
        // Create trial subscription (14 days)
        const trialEndDate = new Date();
        trialEndDate.setDate(trialEndDate.getDate() + 14);
        
        await setDoc(doc(db, 'companySettings', userCredential.user.uid), {
          userId: userCredential.user.uid,
          companyName: companyName,
          email: email,
          status: 'trial',
          trialEndDate: trialEndDate,
          paymentStatus: 'none',
          plan: 'trial',
          pricingSettings: { laborPercentage: 65, profitMargin: 15, materialMarkup: 0 },
          createdAt: new Date()
        });
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      setAuthError(error.message);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setFences([]);
      setSelectedFence(null);
      setPropertyImageUrl(null);
      setEstimate(null);
      setSubscription(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const savePricingSettings = async () => {
    if (user) {
      try {
        await setDoc(doc(db, 'companySettings', user.uid), {
          pricingSettings: pricingSettings,
          updatedAt: new Date()
        }, { merge: true });
      } catch (error) {
        console.error('Error saving settings:', error);
      }
    }
    setShowPricingSettings(false);
  };

  // ADMIN: Update user subscription
  const updateUserSubscription = async (userId, updates) => {
    try {
      await setDoc(doc(db, 'companySettings', userId), updates, { merge: true });
      loadAllUsers();
      alert('Subscription updated successfully!');
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('Error updating subscription');
    }
  };

  const handleAddFence = async (e) => {
    e.preventDefault();
    if (!newFence.image) { alert('Please select a fence image'); return; }
    try {
      const imageRef = ref(storage, `fences/${user.uid}/${Date.now()}_${newFence.image.name}`);
      await uploadBytes(imageRef, newFence.image);
      const imageUrl = await getDownloadURL(imageRef);
      await addDoc(collection(db, 'fences'), {
        userId: user.uid, name: newFence.name, type: newFence.type,
        style: newFence.style, color: newFence.color,
        basePrice: parseFloat(newFence.basePrice), imageUrl, createdAt: new Date()
      });
      setNewFence({ name: '', type: '', style: '', color: '', basePrice: '', image: null });
      setShowAddFence(false);
      loadFences(user.uid);
    } catch (error) {
      console.error('Error adding fence:', error);
      alert('Error adding fence. Please try again.');
    }
  };

  const handleDeleteFence = async (fenceId) => {
    if (window.confirm('Are you sure you want to delete this fence?')) {
      try {
        await deleteDoc(doc(db, 'fences', fenceId));
        loadFences(user.uid);
        if (selectedFence?.id === fenceId) setSelectedFence(null);
      } catch (error) {
        console.error('Error deleting fence:', error);
      }
    }
  };

  const handleFenceImageChange = (e) => {
    if (e.target.files && e.target.files[0]) setNewFence({ ...newFence, image: e.target.files[0] });
  };

  const handleBulkImport = async (e) => {
    e.preventDefault();
    if (!csvFile) { alert('Please select a CSV file'); return; }
    setIsUploading(true);
    setBulkImportProgress('Reading CSV file...');
    try {
      const text = await csvFile.text();
      const lines = text.split('\n').filter(line => line.trim());
      const dataLines = lines.slice(1);
      setBulkImportProgress(`Found ${dataLines.length} fences to import...`);
      let successCount = 0, errorCount = 0;
      for (let i = 0; i < dataLines.length; i++) {
        try {
          const [name, type, style, color, basePrice, imageUrl] = dataLines[i].split(',').map(item => item.trim());
          if (!name || !type || !style || !color || !basePrice || !imageUrl) { errorCount++; continue; }
          setBulkImportProgress(`Importing ${i + 1}/${dataLines.length}: ${name}...`);
          const imageResponse = await fetch(imageUrl);
          const imageBlob = await imageResponse.blob();
          const imageRef = ref(storage, `fences/${user.uid}/${Date.now()}_${name.replace(/\s/g, '_')}.jpg`);
          await uploadBytes(imageRef, imageBlob);
          const uploadedImageUrl = await getDownloadURL(imageRef);
          await addDoc(collection(db, 'fences'), {
            userId: user.uid, name, type, style, color,
            basePrice: parseFloat(basePrice), imageUrl: uploadedImageUrl, createdAt: new Date()
          });
          successCount++;
        } catch (error) { errorCount++; }
      }
      setBulkImportProgress(`Import complete! ${successCount} successful, ${errorCount} errors.`);
      setTimeout(() => {
        setShowBulkImport(false); setCsvFile(null);
        setBulkImportProgress(''); setIsUploading(false);
        loadFences(user.uid);
      }, 3000);
    } catch (error) {
      setBulkImportProgress('Error processing file. Please check format.');
      setIsUploading(false);
    }
  };

  const handleCsvFileChange = (e) => {
    if (e.target.files && e.target.files[0]) setCsvFile(e.target.files[0]);
  };

  const handlePropertyImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (event) => setPropertyImageUrl(event.target.result);
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  useEffect(() => {
    if (propertyImageUrl && selectedFence && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const propertyImg = new Image();
      propertyImg.src = propertyImageUrl;
      propertyImg.onload = () => {
        canvas.width = propertyImg.width;
        canvas.height = propertyImg.height;
        ctx.drawImage(propertyImg, 0, 0);
        if (selectedFence.imageUrl) {
          const fenceImg = new Image();
          fenceImg.crossOrigin = 'anonymous';
          fenceImg.src = selectedFence.imageUrl;
          fenceImg.onload = () => {
            const fenceHeightPx = canvas.height * 0.4;
            const fenceAspectRatio = fenceImg.width / fenceImg.height;
            const fenceWidthPx = fenceHeightPx * fenceAspectRatio;
            const fenceY = canvas.height * 0.55;
            const numTiles = Math.ceil(canvas.width / fenceWidthPx) + 1;
            ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
            ctx.shadowBlur = 8;
            ctx.shadowOffsetY = 4;
            for (let i = 0; i < numTiles; i++) {
              ctx.drawImage(fenceImg, i * fenceWidthPx - 2, fenceY, fenceWidthPx + 4, fenceHeightPx);
            }
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetY = 0;
          };
        }
      };
    }
  }, [propertyImageUrl, selectedFence]);

  const calculateEstimate = () => {
    if (!selectedFence || !linearFeet || !zipCode) {
      alert('Please select a fence, enter linear feet, and ZIP code');
      return;
    }
    const feet = parseFloat(linearFeet);
    const zip3 = zipCode.substring(0, 3);
    const zipMultiplier = ZIP_MULTIPLIERS[zip3] || 1.0;
    const heightMultipliers = { 3: 0.7, 4: 0.85, 5: 0.95, 6: 1.0, 7: 1.15, 8: 1.3 };
    const heightMult = heightMultipliers[fenceHeight] || 1.0;
    const baseMaterialCost = selectedFence.basePrice * feet * heightMult * zipMultiplier;
    const materialCost = baseMaterialCost * (1 + pricingSettings.materialMarkup / 100);
    const laborCost = materialCost * (pricingSettings.laborPercentage / 100);
    const subtotal = materialCost + laborCost;
    const profitAmount = subtotal * (pricingSettings.profitMargin / 100);
    const total = subtotal + profitAmount;
    setEstimate({
      materials: materialCost.toFixed(2),
      labor: laborCost.toFixed(2),
      subtotal: subtotal.toFixed(2),
      profit: profitAmount.toFixed(2),
      total: total.toFixed(2),
      perFoot: (total / feet).toFixed(2)
    });
  };

  const getCustomerLink = () => `${window.location.origin}/estimate/${user.uid}`;

  const copyCustomerLink = () => {
    navigator.clipboard.writeText(getCustomerLink());
    setCustomerLinkCopied(true);
    setTimeout(() => setCustomerLinkCopied(false), 3000);
  };

  // LOGIN PAGE
  if (!user) {
    return (
      <div className="App">
        <div className="auth-container">
          <div className="auth-box">
            <h1>🏗️ Fence Estimator SaaS</h1>
            <h2>{isSignUp ? 'Start Free Trial' : 'Sign In'}</h2>
            {isSignUp && (
              <div style={{background: '#e8f5e9', padding: '12px', borderRadius: '8px', marginBottom: '15px', fontSize: '14px', color: '#2e7d32'}}>
                🎉 <strong>14-Day Free Trial</strong> - No credit card required!
              </div>
            )}
            <form onSubmit={handleAuth}>
              {isSignUp && (
                <input type="text" placeholder="Company Name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
              )}
              <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              {authError && <div className="error">{authError}</div>}
              <button type="submit" className="btn-primary">
                {isSignUp ? 'Start Free Trial' : 'Sign In'}
              </button>
            </form>
            <button onClick={() => setIsSignUp(!isSignUp)} className="btn-link">
              {isSignUp ? 'Already have an account? Sign In' : 'Need an account? Start Free Trial'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // LOADING SUBSCRIPTION
  if (subscriptionLoading) {
    return (
      <div className="App">
        <div className="auth-container">
          <div className="auth-box">
            <h2>Loading your account...</h2>
          </div>
        </div>
      </div>
    );
  }

  // SUBSCRIPTION EXPIRED/SUSPENDED PAGE
  if (!isAdmin && subscription && !isSubscriptionActive(subscription)) {
    const isExpiredTrial = subscription.status === 'trial' && getDaysRemaining(subscription.trialEndDate) === 0;
    const isPaymentFailed = subscription.paymentStatus === 'failed';
    const isSuspended = subscription.status === 'suspended';

    return (
      <div className="App">
        <div className="auth-container">
          <div className="auth-box" style={{maxWidth: '500px'}}>
            <h1>🏗️ Fence Estimator SaaS</h1>
            
            {isExpiredTrial && (
              <>
                <div style={{fontSize: '48px', textAlign: 'center', margin: '20px 0'}}>⏰</div>
                <h2 style={{color: '#e74c3c', textAlign: 'center'}}>Trial Expired</h2>
                <p style={{textAlign: 'center', color: '#666', marginBottom: '20px'}}>
                  Your 14-day free trial has ended. Upgrade to continue using Fence Estimator SaaS!
                </p>
              </>
            )}

            {isPaymentFailed && (
              <>
                <div style={{fontSize: '48px', textAlign: 'center', margin: '20px 0'}}>💳</div>
                <h2 style={{color: '#e74c3c', textAlign: 'center'}}>Payment Failed</h2>
                <p style={{textAlign: 'center', color: '#666', marginBottom: '20px'}}>
                  Your last payment failed. Please update your payment method to restore access.
                </p>
              </>
            )}

            {isSuspended && (
              <>
                <div style={{fontSize: '48px', textAlign: 'center', margin: '20px 0'}}>🔒</div>
                <h2 style={{color: '#e74c3c', textAlign: 'center'}}>Account Suspended</h2>
                <p style={{textAlign: 'center', color: '#666', marginBottom: '20px'}}>
                  Your account has been suspended. Please contact support to restore access.
                </p>
              </>
            )}

            <div style={{background: '#f8f9fa', padding: '20px', borderRadius: '12px', marginBottom: '20px'}}>
              <h3 style={{marginBottom: '15px', color: '#333'}}>Choose Your Plan:</h3>
              
              <div style={{border: '2px solid #667eea', borderRadius: '10px', padding: '15px', marginBottom: '10px', background: 'white'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <h4 style={{margin: '0', color: '#667eea'}}>Starter</h4>
                    <p style={{margin: '5px 0', fontSize: '13px', color: '#666'}}>1 user, unlimited estimates</p>
                  </div>
                  <div style={{textAlign: 'right'}}>
                    <p style={{margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#333'}}>$49</p>
                    <p style={{margin: '0', fontSize: '12px', color: '#666'}}>/month</p>
                  </div>
                </div>
              </div>

              <div style={{border: '2px solid #764ba2', borderRadius: '10px', padding: '15px', background: 'white'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <h4 style={{margin: '0', color: '#764ba2'}}>Pro ⭐</h4>
                    <p style={{margin: '5px 0', fontSize: '13px', color: '#666'}}>3 users, priority support</p>
                  </div>
                  <div style={{textAlign: 'right'}}>
                    <p style={{margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#333'}}>$99</p>
                    <p style={{margin: '0', fontSize: '12px', color: '#666'}}>/month</p>
                  </div>
                </div>
              </div>
            </div>

            <a 
              href="mailto:contact@simplyawesomemarketing.com?subject=Fence Estimator SaaS - Upgrade Request"
              style={{
                display: 'block', width: '100%', padding: '15px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white', textAlign: 'center', borderRadius: '10px',
                textDecoration: 'none', fontWeight: 'bold', fontSize: '16px',
                marginBottom: '10px'
              }}
            >
              📧 Contact Us to Upgrade
            </a>

            <button onClick={handleSignOut} className="btn-link" style={{width: '100%', textAlign: 'center'}}>
              Sign Out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ADMIN PANEL
  if (isAdmin && showAdminPanel) {
    return (
      <div className="App">
        <header className="app-header">
          <h1>🔧 Admin Panel</h1>
          <div className="header-right">
            <button onClick={() => setShowAdminPanel(false)} className="btn-secondary">Back to App</button>
            <button onClick={handleSignOut} className="btn-secondary">Sign Out</button>
          </div>
        </header>

        <div className="main-content">
          <div className="section">
            <h2>👥 All Companies ({allUsers.length})</h2>
            
            {allUsers.map((u) => {
              const daysLeft = getDaysRemaining(u.trialEndDate);
              const active = isSubscriptionActive(u);
              
              return (
                <div key={u.id} style={{
                  border: `2px solid ${active ? '#27ae60' : '#e74c3c'}`,
                  borderRadius: '12px', padding: '20px', marginBottom: '15px',
                  background: active ? '#f0fff4' : '#fff5f5'
                }}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px'}}>
                    <div>
                      <h3 style={{margin: '0 0 5px 0'}}>{u.companyName || 'Unknown Company'}</h3>
                      <p style={{margin: '0', fontSize: '14px', color: '#666'}}>{u.email}</p>
                      <p style={{margin: '5px 0', fontSize: '13px'}}>
                        Status: <strong style={{color: active ? '#27ae60' : '#e74c3c'}}>
                          {u.status === 'trial' ? `Trial (${daysLeft} days left)` : u.status}
                        </strong>
                      </p>
                      <p style={{margin: '0', fontSize: '13px', color: '#666'}}>
                        Payment: {u.paymentStatus} | Plan: {u.plan}
                      </p>
                    </div>

                    <div style={{display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '200px'}}>
                      <button
                        onClick={() => updateUserSubscription(u.id, {
                          status: 'active', paymentStatus: 'paid', plan: 'starter'
                        })}
                        style={{padding: '8px 15px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px'}}
                      >
                        ✅ Activate (Paid)
                      </button>

                      <button
                        onClick={() => {
                          const days = prompt('Extend trial by how many days?', '14');
                          if (days) {
                            const newEnd = new Date();
                            newEnd.setDate(newEnd.getDate() + parseInt(days));
                            updateUserSubscription(u.id, { status: 'trial', trialEndDate: newEnd, paymentStatus: 'none' });
                          }
                        }}
                        style={{padding: '8px 15px', background: '#667eea', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px'}}
                      >
                        ⏰ Extend Trial
                      </button>

                      <button
                        onClick={() => updateUserSubscription(u.id, { status: 'suspended', paymentStatus: 'failed' })}
                        style={{padding: '8px 15px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px'}}
                      >
                        🔒 Suspend Access
                      </button>

                      <button
                        onClick={() => updateUserSubscription(u.id, { paymentStatus: 'failed' })}
                        style={{padding: '8px 15px', background: '#f39c12', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px'}}
                      >
                        💳 Mark Payment Failed
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // MAIN APP
  return (
    <div className="App">
      <header className="app-header">
        <h1>🏗️ Fence Estimator SaaS</h1>
        <div className="header-right">
          {/* Trial Banner */}
          {subscription?.status === 'trial' && (
            <div style={{
              background: getDaysRemaining(subscription.trialEndDate) <= 3 ? '#e74c3c' : '#f39c12',
              color: 'white', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold'
            }}>
              ⏰ Trial: {getDaysRemaining(subscription.trialEndDate)} days left
            </div>
          )}

          {/* Admin Button */}
          {isAdmin && (
            <button
              onClick={() => { setShowAdminPanel(true); loadAllUsers(); }}
              style={{marginRight: '10px', padding: '8px 16px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'}}
            >
              🔧 Admin Panel
            </button>
          )}

          <span style={{fontSize: '14px'}}>{subscription?.companyName || user.email}</span>
          <button
            onClick={() => setShowCustomerLink(true)}
            className="btn-primary"
            style={{marginLeft: '10px', background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)'}}
          >
            🔗 Get Customer Link
          </button>
          <button onClick={handleSignOut} className="btn-secondary" style={{marginLeft: '10px'}}>Sign Out</button>
        </div>
      </header>

      <div className="main-content">

        {/* Customer Link Modal */}
        {showCustomerLink && (
          <div className="modal-overlay" onClick={() => setShowCustomerLink(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>🔗 Your Customer Estimator Link</h3>
              <p style={{color: '#666', marginBottom: '20px', fontSize: '14px'}}>
                Share this with customers! They'll see your fences and get a quote - without seeing your profit margins.
              </p>
              <div style={{background: '#f0f8ff', border: '2px solid #667eea', borderRadius: '8px', padding: '15px', marginBottom: '20px', wordBreak: 'break-all', fontSize: '14px'}}>
                {getCustomerLink()}
              </div>
              <div style={{display: 'flex', gap: '10px', marginBottom: '20px'}}>
                <button onClick={copyCustomerLink} className="btn-primary" style={{flex: 1}}>
                  {customerLinkCopied ? '✅ Copied!' : '📋 Copy Link'}
                </button>
                <button onClick={() => window.open(getCustomerLink(), '_blank')} className="btn-secondary" style={{flex: 1}}>
                  👁️ Preview
                </button>
              </div>
              <div style={{background: '#f9f9f9', padding: '15px', borderRadius: '8px', fontSize: '13px'}}>
                <p style={{margin: '0 0 8px 0'}}><strong>✅ Customers CAN see:</strong> Your fence styles, final price, contact form</p>
                <p style={{margin: '0', color: '#e74c3c'}}><strong>❌ Customers CANNOT see:</strong> Your profit %, labor %, markup %</p>
              </div>
              <button onClick={() => setShowCustomerLink(false)} className="btn-secondary" style={{width: '100%', marginTop: '15px'}}>Close</button>
            </div>
          </div>
        )}

        {/* Fence Library */}
        <div className="section">
          <div className="section-header">
            <h2>📚 Your Fence Library</h2>
            <div>
              <button onClick={() => setShowAddFence(true)} className="btn-primary">+ Add Fence</button>
              <button onClick={() => setShowBulkImport(true)} className="btn-secondary" style={{marginLeft: '10px'}}>📤 Bulk Import</button>
            </div>
          </div>

          {showAddFence && (
            <div className="modal-overlay" onClick={() => setShowAddFence(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Add New Fence</h3>
                <form onSubmit={handleAddFence}>
                  <input type="text" placeholder="Fence Name" value={newFence.name} onChange={(e) => setNewFence({...newFence, name: e.target.value})} required />
                  <input type="text" placeholder="Type (e.g., Wood, Vinyl, Chain Link)" value={newFence.type} onChange={(e) => setNewFence({...newFence, type: e.target.value})} required />
                  <input type="text" placeholder="Style (e.g., Privacy, Picket, Split Rail)" value={newFence.style} onChange={(e) => setNewFence({...newFence, style: e.target.value})} required />
                  <input type="text" placeholder="Color" value={newFence.color} onChange={(e) => setNewFence({...newFence, color: e.target.value})} required />
                  <input type="number" step="0.01" placeholder="Base Price per Linear Foot" value={newFence.basePrice} onChange={(e) => setNewFence({...newFence, basePrice: e.target.value})} required />
                  <input type="file" accept="image/*" onChange={handleFenceImageChange} required />
                  <div className="modal-buttons">
                    <button type="submit" className="btn-primary">Add Fence</button>
                    <button type="button" onClick={() => setShowAddFence(false)} className="btn-secondary">Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showBulkImport && (
            <div className="modal-overlay" onClick={() => !isUploading && setShowBulkImport(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>📤 Bulk Import Fences</h3>
                <div style={{marginBottom: '20px', padding: '15px', background: '#f0f0f0', borderRadius: '8px'}}>
                  <code style={{display: 'block', padding: '10px', background: 'white', borderRadius: '5px', fontSize: '12px'}}>
                    Name, Type, Style, Color, Base Price, Image URL
                  </code>
                </div>
                <form onSubmit={handleBulkImport}>
                  <input type="file" accept=".csv" onChange={handleCsvFileChange} disabled={isUploading} required />
                  {bulkImportProgress && (
                    <div style={{marginTop: '15px', padding: '10px', background: '#e3f2fd', borderRadius: '5px', color: '#1976d2'}}>
                      {bulkImportProgress}
                    </div>
                  )}
                  <div className="modal-buttons">
                    <button type="submit" className="btn-primary" disabled={isUploading}>{isUploading ? 'Importing...' : 'Import Fences'}</button>
                    <button type="button" onClick={() => setShowBulkImport(false)} className="btn-secondary" disabled={isUploading}>Cancel</button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {showPricingSettings && (
            <div className="modal-overlay" onClick={() => setShowPricingSettings(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>⚙️ Pricing Settings</h3>
                <p style={{fontSize: '14px', color: '#e74c3c', marginBottom: '20px', background: '#fff5f5', padding: '10px', borderRadius: '8px'}}>
                  🔒 These settings are PRIVATE - customers only see the final quote.
                </p>
                <div className="input-group">
                  <label>Labor Percentage:</label>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <input type="range" min="0" max="100" value={pricingSettings.laborPercentage} onChange={(e) => setPricingSettings({...pricingSettings, laborPercentage: parseInt(e.target.value)})} style={{flex: 1}} />
                    <input type="number" value={pricingSettings.laborPercentage} onChange={(e) => setPricingSettings({...pricingSettings, laborPercentage: parseInt(e.target.value) || 0})} style={{width: '70px'}} />
                    <span>%</span>
                  </div>
                </div>
                <div className="input-group">
                  <label>Profit Margin:</label>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <input type="range" min="0" max="50" value={pricingSettings.profitMargin} onChange={(e) => setPricingSettings({...pricingSettings, profitMargin: parseInt(e.target.value)})} style={{flex: 1}} />
                    <input type="number" value={pricingSettings.profitMargin} onChange={(e) => setPricingSettings({...pricingSettings, profitMargin: parseInt(e.target.value) || 0})} style={{width: '70px'}} />
                    <span>%</span>
                  </div>
                </div>
                <div className="input-group">
                  <label>Material Markup:</label>
                  <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                    <input type="range" min="0" max="50" value={pricingSettings.materialMarkup} onChange={(e) => setPricingSettings({...pricingSettings, materialMarkup: parseInt(e.target.value)})} style={{flex: 1}} />
                    <input type="number" value={pricingSettings.materialMarkup} onChange={(e) => setPricingSettings({...pricingSettings, materialMarkup: parseInt(e.target.value) || 0})} style={{width: '70px'}} />
                    <span>%</span>
                  </div>
                </div>
                <div className="modal-buttons" style={{marginTop: '20px'}}>
                  <button onClick={savePricingSettings} className="btn-primary">Save Settings</button>
                  <button onClick={() => setPricingSettings({laborPercentage: 65, profitMargin: 15, materialMarkup: 0})} className="btn-secondary">Reset</button>
                </div>
              </div>
            </div>
          )}

          <div className="fence-grid">
            {fences.map((fence) => (
              <div key={fence.id} className={`fence-card ${selectedFence?.id === fence.id ? 'selected' : ''}`} onClick={() => setSelectedFence(fence)}>
                <img src={fence.imageUrl} alt={fence.name} />
                <h3>{fence.name}</h3>
                <p>{fence.type} - {fence.style}</p>
                <p>Color: {fence.color}</p>
                <p className="price">${fence.basePrice}/ft</p>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteFence(fence.id); }} className="btn-delete">Delete</button>
              </div>
            ))}
          </div>
        </div>

        {/* Property Visualization */}
        <div className="section">
          <h2>🏠 Property Visualization</h2>
          <div className="upload-section">
            <label className="file-upload">
              <input type="file" accept="image/*" onChange={handlePropertyImageChange} />
              📸 Upload Property Image
            </label>
          </div>
          {propertyImageUrl && <div className="canvas-container"><canvas ref={canvasRef}></canvas></div>}
        </div>

        {/* Estimate Calculator */}
        <div className="section">
          <h2>💰 Generate Estimate</h2>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
            <button onClick={() => setShowPricingSettings(true)} className="btn-secondary" style={{fontSize: '14px', padding: '8px 16px'}}>
              ⚙️ Pricing Settings
            </button>
            <div style={{fontSize: '14px', color: '#666'}}>
              Labor: {pricingSettings.laborPercentage}% | Profit: {pricingSettings.profitMargin}% | Markup: {pricingSettings.materialMarkup}%
            </div>
          </div>
          <div className="calculator">
            <div className="input-group">
              <label>Fence Height (ft):</label>
              <select value={fenceHeight} onChange={(e) => setFenceHeight(parseInt(e.target.value))}>
                <option value="3">3 ft</option><option value="4">4 ft</option>
                <option value="5">5 ft</option><option value="6">6 ft</option>
                <option value="7">7 ft</option><option value="8">8 ft</option>
              </select>
            </div>
            <div className="input-group">
              <label>Linear Feet:</label>
              <input type="number" value={linearFeet} onChange={(e) => setLinearFeet(e.target.value)} placeholder="Enter linear feet" />
            </div>
            <div className="input-group">
              <label>ZIP Code:</label>
              <input type="text" value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="Enter ZIP code" maxLength="5" />
            </div>
            <button onClick={calculateEstimate} className="btn-primary btn-large">Calculate Estimate</button>
            {estimate && (
              <div className="estimate-result">
                <h3>📋 Estimate Breakdown</h3>
                <div className="estimate-line"><span>Materials:</span><span>${estimate.materials}</span></div>
                <div className="estimate-line"><span>Labor ({pricingSettings.laborPercentage}%):</span><span>${estimate.labor}</span></div>
                <div className="estimate-line"><span>Subtotal:</span><span>${estimate.subtotal}</span></div>
                {parseFloat(estimate.profit) > 0 && (
                  <div className="estimate-line"><span>Profit ({pricingSettings.profitMargin}%):</span><span>${estimate.profit}</span></div>
                )}
                <div className="estimate-line total"><span>Total:</span><span>${estimate.total}</span></div>
                <div className="estimate-line"><span>Per Linear Foot:</span><span>${estimate.perFoot}</span></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
